import type { FormikHelpers } from 'formik';
import type { FieldValues, Path, PathValue, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { formatDashedYYYYMMDD } from 'utils';
import type { PassengerValues } from '~/components/PassengerDetailsForm';

const AUTOFILLABLE_INPUT_TYPES = new Set(['text', 'email', 'tel', 'url', 'search']);

export const syncAutofilledInputs = <T extends FieldValues>(
  form: HTMLFormElement | null,
  getValues: UseFormGetValues<T>,
  setValue: UseFormSetValue<T>
) => {
  if (!form) {
    return;
  }

  const values = getValues() as Record<string, unknown>;

  const selector = 'input[name]:not([aria-hidden="true"]):not([tabindex="-1"])';

  for (const input of form.querySelectorAll<HTMLInputElement>(selector)) {
    const current = values[input.name];
    if (!AUTOFILLABLE_INPUT_TYPES.has(input.type) || typeof current !== 'string' || current === input.value) {
      continue;
    }
    setValue(input.name as Path<T>, input.value as PathValue<T, Path<T>>);
  }
};

export const generateTestData = (
  pax,
  shouldAskForPassport,
  shouldAskForPassportExpiryAndIssueDate,
  travellerMix: any = null,
  tripEndDate?: string
) => {
  const testPassengers = pax.map((passenger, i) => {
    // Create valid test data that passes all validation checks
    const tripEnd = tripEndDate ? new Date(tripEndDate) : new Date();
    const endYear = tripEnd.getFullYear();
    const endMonth = tripEnd.getMonth();
    const endDay = tripEnd.getDate();
    let age: number;

    if (passenger.type === 'Adult') {
      // Adults: random ages 25-65 (definitely over 18 at booking and trip time)
      age = Math.floor(Math.random() * 40) + 25;
    } else {
      // Children: use ages from travellerMix if available, otherwise use sensible defaults
      if (travellerMix && travellerMix.childAges && travellerMix.childAges[passenger.index] !== undefined) {
        age = travellerMix.childAges[passenger.index];
      } else {
        // Fallback to sensible child ages if travellerMix is not available
        // Use ages 5-17 for children
        age = Math.floor(Math.random() * 13) + 5; // 5-17 years old
      }
    }

    // Calculate birth year based on trip end date and desired age
    const birthYear = endYear - age;

    // For children, ensure birth date is before the trip end date so they have the correct age
    // We need to ensure they have the correct age on the trip end date, not just today
    let birthMonth: number;
    let birthDay: number;

    if (passenger.type === 'Child') {
      // Ensure the child is exactly the specified age on the trip end date:
      // pick DOB as the same month/day as the trip end date but 'age' years earlier
      birthMonth = endMonth + 1; // convert to 1-12 for construction below
      birthDay = endDay;
    } else {
      // For adults, any date is acceptable; choose same month/day as trip end for stability
      birthMonth = endMonth + 1;
      birthDay = endDay;
    }

    // JS Date months are 0-based
    const dateOfBirth = new Date(birthYear, birthMonth - 1, birthDay);

    return {
      gender: 'MALE',
      firstName: 'Test',
      lastName: 'Test',
      passportNumber: shouldAskForPassport ? `ABC${100000 + i}` : '',
      passportExpiryDate: shouldAskForPassportExpiryAndIssueDate
        ? new Date(new Date().setFullYear(new Date().getFullYear() + 5))
        : null,
      passportIssueDate: shouldAskForPassportExpiryAndIssueDate
        ? new Date(new Date().setFullYear(new Date().getFullYear() - 2))
        : null,
      countryOfResidence: 'UK',
      dateOfBirth: dateOfBirth,
      placeOfBirth: 'London, UK',
      nationality: 'UK',
      phoneNumber: '+447911123456',
      railcard: i === 0 ? '16-25' : '',
      accessibilityNeeds: '',
      dietaryRequirements: i === 0 ? 'Vegetarian' : '',
      leadBooker: i === 0 // first passenger is lead booker
    };
  });

  return {
    passengers: testPassengers,
    email: 'test@example.com',
    addressLine1: '123 Test Street',
    addressLine2: 'Flat 1',
    city: 'London',
    county: 'Greater London',
    postcode: 'W1A 1AA',
    country: 'UK',
    bookerFirstName: 'Test',
    bookerLastName: 'Test',
    bookerEmail: 'booker@example.com',
    hearAboutUs: ['Other'] as string[],
    hearAboutUsOther: 'Test',
    firstHearDate: 'Within the last week',
    additionalInfo: 'This is a test booking.',
    referrerName: 'Test Referrer',
    bookingReason: 'Testing! Testing!',
    alternativeHolidayAction: '',
    alternativeHolidayDestination: '',
    transportTimingRequests: 'Testing! Testing!',
    firstChoiceNewsletterConsent: true,
    coOpHolidaysNewsletterConsent: true,
    bywayNewsletterConsent: true,
    // acceptTerms is no longer a form field — submitting implies acceptance and the
    // booking form injects it into the request body. Seed it here so test-data flows
    // that inspect the values object (pre-merge) still cover the accepted state.
    acceptTerms: true,
    tripBookerIsNotLeadBooker: false,
    hasTransportTimingRequests: 'yes'
  };
};

/**
 * Populates the passenger details form with test data
 *
 * @param values Current form values
 * @param setValues Formik setValues function
 * @param setFieldValue Formik setFieldValue function
 * @param pax Array of passenger information (type and index)
 * @param shouldAskForPassport Whether passport information should be requested
 * @param shouldAskForPassportExpiryAndIssueDate Whether passport expiry and issue dates should be requested
 * @param generateTestData Function to generate test data
 * @param travellerMix Traveller mix information
 */
export const populatePassengerFormWithTestData = (
  values: any,
  { setValues }: Pick<FormikHelpers<any>, 'setValues' | 'setFieldValue'>,
  pax: Array<{ type: string; index: number }>,
  shouldAskForPassport: boolean,
  shouldAskForPassportExpiryAndIssueDate: boolean,
  generateTestData: (
    pax: Array<{ type: string; index: number }>,
    shouldAskForPassport: boolean,
    shouldAskForPassportExpiryAndIssueDate: boolean,
    travellerMix?: any
  ) => any,
  travellerMix?: any
) => {
  const testData = generateTestData(pax, shouldAskForPassport, shouldAskForPassportExpiryAndIssueDate, travellerMix);

  // Set all values including dates in one go
  setValues({
    ...values,
    ...testData,
    passengers: testData.passengers?.map((p) => ({
      ...p,
      // Ensure dates are properly set
      dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth) : null,
      passportExpiryDate: p.passportExpiryDate ? new Date(p.passportExpiryDate) : null,
      passportIssueDate: p.passportIssueDate ? new Date(p.passportIssueDate) : null
    })),
    // Override specific values to ensure validation works correctly
    hasTransportTimingRequests: 'yes'
  });

  // Note: Individual date fields (day/month/year) are handled by the DateInput component
  // which derives them from the main dateOfBirth field. No need to set them separately.
};

export const capitalizeWords = (str: string) => {
  return str
    .split(/[-_ ]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'in',
  'nor',
  'of',
  'on',
  'or',
  'per',
  'the',
  'to',
  'vs',
  'via'
]);
export const ACRONYMS = new Set(['UK', 'USA', 'US', 'UAE', 'EU', 'DB', 'SNCF', 'RENFE', 'TGV', 'AVE', 'ICE']);

export const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export const titleCaseWord = (word: string, isEdge: boolean) =>
  word
    .split('-')
    .map((part) => {
      const upper = part.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      const lower = part.toLowerCase();
      const minor = MINOR_WORDS.has(lower);
      if (minor && !isEdge) return lower;
      // handle O'Connor / L'Hospitalet
      return lower
        .split("'")
        .map((seg) => (seg ? cap(seg) : seg))
        .join("'");
    })
    .join('-');

export const toTitleCase = (value?: string) => {
  const v = (value ?? '').trim();
  if (!v) return v;
  const words = v.split(/\s+/);
  return words.map((w, i) => titleCaseWord(w, i === 0 || i === words.length - 1)).join(' ');
};

export const toSentenceCase = (value?: string) => {
  const v = (value ?? '').trim();
  if (!v) return v;
  const lower = v.toLowerCase();
  return lower[0].toUpperCase() + lower.slice(1);
};

export const formatPlacesList = (value?: string) =>
  (value ?? '')
    .split(',')
    .map((s) => toTitleCase(s.trim()))
    .filter(Boolean)
    .join(', ');

/**
 * A validation warning as a stable code + interpolation params, resolved to a localized string by
 * the caller via `t('content.feedback.' + code, params)`. Keeps these helpers pure/testable.
 */
export type FeedbackRef = { code: string; params?: Record<string, string | number> };

type DuplicateWarningType = 'name' | 'dob';

interface DuplicateWarning {
  type: DuplicateWarningType;
  passengerIndex: number;
  duplicateOfIndex: number;
}

export const getDuplicatePassengerErrors = (
  passengers: Array<{ firstName: string; lastName: string; dateOfBirth: Date | null }>
) => {
  const normaliseName = (name: string) => name.trim().toLowerCase();

  const nameMap = new Map<string, number>();
  const dobMap = new Map<string, number>();

  const passengerErrors: Array<Record<string, FeedbackRef>> = [];

  const addWarning = (warning: DuplicateWarning) => {
    const index = warning.passengerIndex;
    const duplicateOfIndex = warning.duplicateOfIndex;
    if (!passengerErrors[index]) {
      passengerErrors[index] = {};
    }

    // The current passenger is the array position (index); we pass the matched passenger's index so
    // the caller can compose + localize both labels ("Adult 1", "Child 2") from its own passenger meta.
    if (warning.type === 'name') {
      passengerErrors[index].firstName = { code: 'duplicate_name', params: { duplicateOfIndex } };
    }

    if (warning.type === 'dob') {
      passengerErrors[index].dateOfBirth = { code: 'duplicate_dob', params: { duplicateOfIndex } };
    }
  };

  passengers.forEach((passenger, index) => {
    const firstName = normaliseName(passenger.firstName || '');
    const lastName = normaliseName(passenger.lastName || '');

    if (firstName && lastName) {
      const nameKey = `${firstName}::${lastName}`;
      if (nameMap.has(nameKey)) {
        const duplicateOfIndex = nameMap.get(nameKey) as number;
        addWarning({ type: 'name', passengerIndex: index, duplicateOfIndex });
      } else {
        nameMap.set(nameKey, index);
      }
    }

    if (passenger.dateOfBirth instanceof Date && !Number.isNaN(passenger.dateOfBirth.getTime())) {
      const dobKey = formatDashedYYYYMMDD(passenger.dateOfBirth);
      if (dobMap.has(dobKey)) {
        const duplicateOfIndex = dobMap.get(dobKey) as number;
        addWarning({ type: 'dob', passengerIndex: index, duplicateOfIndex });
      } else {
        dobMap.set(dobKey, index);
      }
    }
  });

  if (passengerErrors.length === 0) {
    return null;
  }

  return {
    passengers: passengerErrors
  };
};

type PassengerErrors = { passengers: Record<string, FeedbackRef>[] };

export const getAfghanistanPassengerErrors = (
  passengers: Array<{ countryOfResidence: string }>
): PassengerErrors | null => {
  if (!passengers.some((p) => p.countryOfResidence === 'AF')) return null;

  return {
    passengers: passengers.map((p) =>
      p.countryOfResidence === 'AF'
        ? { countryOfResidence: { code: 'country_afghanistan' } }
        : ({} as Record<string, FeedbackRef>)
    )
  };
};

export const mergePassengerErrors = (...errorSets: Array<PassengerErrors | null>): PassengerErrors | null => {
  const valid = errorSets.filter((e): e is PassengerErrors => e !== null);
  if (valid.length === 0) return null;

  const length = Math.max(...valid.map((e) => e.passengers.length));
  return {
    passengers: Array.from({ length }, (_, i) =>
      valid.reduce<Record<string, FeedbackRef>>((acc, e) => Object.assign(acc, e.passengers[i] ?? {}), {})
    )
  };
};

type PassengerWarningResult = {
  errors: PassengerErrors;
  acknowledgedDuplicate: boolean;
  acknowledgedAfghanistan: boolean;
};

export const getPassengerSubmitWarnings = (
  passengers: Array<{ firstName: string; lastName: string; dateOfBirth: Date | null; countryOfResidence: string }>,
  duplicateWarningAcknowledged: boolean,
  countryOfResidenceWarningAcknowledged: boolean
): PassengerWarningResult | null => {
  const duplicateErrors = getDuplicatePassengerErrors(
    passengers.map((p) => ({ firstName: p.firstName, lastName: p.lastName, dateOfBirth: p.dateOfBirth }))
  );
  const afghaniErrors = getAfghanistanPassengerErrors(passengers);

  const shouldWarnDuplicate = !!duplicateErrors && !duplicateWarningAcknowledged;
  const shouldWarnAfghanistan = !!afghaniErrors && !countryOfResidenceWarningAcknowledged;

  if (!shouldWarnDuplicate && !shouldWarnAfghanistan) return null;

  return {
    errors: mergePassengerErrors(
      shouldWarnDuplicate ? duplicateErrors : null,
      shouldWarnAfghanistan ? afghaniErrors : null
    ) as PassengerErrors,
    acknowledgedDuplicate: shouldWarnDuplicate,
    acknowledgedAfghanistan: shouldWarnAfghanistan
  };
};

export const getLeadBooker = (passengers: PassengerValues[]) => passengers.find((p) => p.leadBooker === true);

export const getLeadBookerCountryOfResidence = (passengers: PassengerValues[]): string => {
  const leadBooker = getLeadBooker(passengers);
  const cor = leadBooker?.countryOfResidence ?? null;
  if (!cor) throw new Error('No country of residence found for lead passenger');
  return cor;
};
